import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import {
  Lock, Mail, Eye, EyeOff, Sun, Moon, ArrowRight, Landmark, Home, Gavel
} from "lucide-react";
import { listDemoUsers, login } from "../api";
import { useTheme } from "./Theme";
import { useVersion } from "./Version";
import Register from "./Register";

const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === "true";

const ROLE_ORDER = [
  "ADMIN","PRESIDENT","VICE_PRESIDENT","FINANCE_HEAD",
  "CRICKET_HEAD","VOLLEYBALL_HEAD","CAPTAIN","PLAYER"
];

function highestRole(roles) {
  for (const r of ROLE_ORDER) if (roles.includes(r)) return r;
  return null;
}

const BRAND_LETTERS = "GABRUZ UNIVERSE".split("");

// ============================================================================
// IntroOverlay — full-screen cinematic that plays once per browser session
// before the login page reveals. Uses framer-motion to choreograph:
//   1. logo drops in from above with a slight scale + rotate
//   2. gold halos pulse outward from the logo
//   3. "GABRUZ UNIVERSE" letters cascade in
//   4. tagline fades in
//   5. whole stack zooms out + fades, revealing the login behind
// Respects prefers-reduced-motion (skips the choreography entirely).
// ============================================================================
const INTRO_TOTAL_MS = 5000;

function IntroOverlay({ onDone }) {
  const reduced = useReducedMotion();

  useEffect(() => {
    // Total runtime 5.0s. With reduced motion, end immediately.
    if (reduced) { onDone(); return; }
    const t = setTimeout(onDone, INTRO_TOTAL_MS);
    return () => clearTimeout(t);
  }, [reduced, onDone]);

  if (reduced) return null;

  return (
    <motion.div
      className="loginIntro"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.05 }}
      transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
    >
      {/* Animated background — three large color orbs that drift while
          the intro plays, fading the page in. */}
      <div className="loginIntro-bg" aria-hidden="true">
        <motion.span
          className="loginIntro-orb loginIntro-orb--gold"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 0.55, scale: 1 }}
          transition={{ duration: 1.4, ease: "easeOut" }}
        />
        <motion.span
          className="loginIntro-orb loginIntro-orb--blue"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 0.45, scale: 1 }}
          transition={{ duration: 1.6, delay: 0.15, ease: "easeOut" }}
        />
        <motion.span
          className="loginIntro-orb loginIntro-orb--red"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 0.3, scale: 1 }}
          transition={{ duration: 1.8, delay: 0.3, ease: "easeOut" }}
        />
      </div>

      <button
        type="button"
        className="loginIntro-skip"
        onClick={onDone}
        aria-label="Skip intro"
      >
        Skip ›
      </button>

      <div className="loginIntro-stage">
        {/* Hero brand entrance — pure fade + subtle scale. No blur
            filter (which can change containing-block behaviour on
            fixed-positioned children in some browsers). */}
        <motion.div
          className="loginIntro-logoWrap"
          initial={{ opacity: 0, scale: 1.03 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 1.2,
            delay: 0.2,
            ease: [0.22, 1, 0.36, 1]
          }}
        >
          {/* Halos pulse AFTER the dolly zoom completes (delay 2.0s)
              so they read as the logo "landing" into place. */}
          <motion.span
            className="loginIntro-halo loginIntro-halo--1"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 2.4, opacity: [0, 0.75, 0] }}
            transition={{ duration: 2.4, delay: 2.0, ease: "easeOut", times: [0, 0.2, 1] }}
          />
          <motion.span
            className="loginIntro-halo loginIntro-halo--2"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 3.0, opacity: [0, 0.55, 0] }}
            transition={{ duration: 2.6, delay: 2.25, ease: "easeOut", times: [0, 0.2, 1] }}
          />
          <motion.span
            className="loginIntro-halo loginIntro-halo--3"
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 3.6, opacity: [0, 0.38, 0] }}
            transition={{ duration: 2.8, delay: 2.5, ease: "easeOut", times: [0, 0.2, 1] }}
          />

          {/* Hero brand art — contains the wordmark, tagline, skyline
              and values ribbon, so we don't render duplicate versions
              of those below. Falls back to the compact /logo.png if
              the hero asset can't be loaded. */}
          <img
            src="/gabruz-brand.png"
            alt="Gabruz Universe"
            className="loginIntro-logo loginIntro-logo--hero"
            onError={e => {
              if (e.currentTarget.dataset.fell) {
                e.currentTarget.style.display = "none";
                return;
              }
              e.currentTarget.dataset.fell = "1";
              e.currentTarget.classList.remove("loginIntro-logo--hero");
              e.currentTarget.src = "/logo.png";
            }}
          />
        </motion.div>

        {/* Sparkle burst around logo — small gold dots radiating out */}
        <motion.div
          className="loginIntro-sparks"
          aria-hidden="true"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 1.4, delay: 4.0, times: [0, 0.4, 1] }}
        >
          {Array.from({ length: 16 }).map((_, i) => (
            <span
              key={i}
              className="loginIntro-spark"
              style={{ transform: `rotate(${(360 / 16) * i}deg) translateY(-180px)` }}
            />
          ))}
        </motion.div>
      </div>
    </motion.div>
  );
}

// ============================================================================
// Login — hero-split layout with the big brand on the left.
// ============================================================================
export default function Login({ onLogin }) {
  const [email, setEmail] = useState(DEMO_MODE ? "ashish@gabruzuniverse.com" : "");
  const [password, setPassword] = useState(DEMO_MODE ? "gabruz123" : "");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [demoUsers, setDemoUsers] = useState([]);
  const { theme, toggleTheme } = useTheme();
  const { version, setVersion } = useVersion();
  // Workspace picker removed — everyone lands in the unified Gabruz
  // Universe shell. League and Officiating live inside as sidebar
  // section / dashboard tile, gated by role.

  // Cinematic intro — plays once per browser session. The flag survives
  // SPA navigations within the same tab but resets on a fresh tab/window
  // so first-time visitors and returning users (from a cold tab) both
  // see the show.
  const [showIntro, setShowIntro] = useState(() =>
    !sessionStorage.getItem("gabruz-intro-played")
  );
  const dismissIntro = () => {
    sessionStorage.setItem("gabruz-intro-played", "1");
    setShowIntro(false);
  };

  useEffect(() => {
    if (!DEMO_MODE) return;
    listDemoUsers()
      .then(setDemoUsers)
      .catch(() => setDemoUsers([]));
  }, []);

  const roleButtons = useMemo(() => {
    if (!DEMO_MODE) return [];
    const byRole = new Map();
    for (const u of demoUsers) {
      const top = highestRole(u.roles || []);
      if (!top) continue;
      if (!byRole.has(top)) byRole.set(top, u);
    }
    return ROLE_ORDER.map(role => ({ role, user: byRole.get(role) || null }));
  }, [demoUsers]);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setError("");
    setBusy(true);
    try {
      const r = await login(email, password);
      // Single shell now — everyone lands in Gabruz Universe. League /
      // Officiating tabs auto-appear based on the user's roles.
      sessionStorage.setItem("gabruz-just-logged-in", "1");
      onLogin(r.user);
    } catch (err) {
      setError(err.message || "Sign-in failed. Check your email and password.");
      setBusy(false);
    }
  }

  function pickRole(user) {
    if (!user) return;
    setEmail(user.email);
    setPassword("gabruz123");
    setError("");
  }

  // When the user picks "Apply to join", replace the whole login screen
  // with the registration form until they submit or cancel.
  if (showRegister) {
    return <Register onBack={() => setShowRegister(false)}/>;
  }

  return (
    <>
      <AnimatePresence>
        {showIntro && <IntroOverlay key="intro" onDone={dismissIntro} />}
      </AnimatePresence>

      <div className="loginV2">
        {/* ── LEFT — brand hero ──────────────────────────────── */}
        <aside className="loginV2-hero">
          <div className="loginV2-hero-bg" aria-hidden="true">
            <span className="loginV2-hero-orb loginV2-hero-orb--a"/>
            <span className="loginV2-hero-orb loginV2-hero-orb--b"/>
            <span className="loginV2-hero-orb loginV2-hero-orb--c"/>
          </div>

          {/* The full gabruz-brand.png artwork covers the entire left
              panel — no supporting copy or footer here since the
              illustration already carries the wordmark, tagline and
              values ribbon. Falls back to the compact /logo.png if
              the hero asset fails to load. */}
          <motion.div
            className="loginV2-hero-brandArt loginV2-hero-brandArt--fill"
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: showIntro ? 5.0 : 0.2, ease: [0.4, 0, 0.2, 1] }}
          >
            <img
              src="/gabruz-brand.png"
              alt="Gabruz Universe — United by passion. Connected for life."
              className="loginV2-hero-brandImg loginV2-hero-brandImg--cover"
              onError={e => {
                // Two-tier fallback: hero art → compact logo, then hide.
                if (e.currentTarget.dataset.fell) {
                  e.currentTarget.style.display = "none";
                  return;
                }
                e.currentTarget.dataset.fell = "1";
                e.currentTarget.classList.add("loginV2-hero-brandImg--fallback");
                e.currentTarget.src = "/logo.png";
              }}
            />
          </motion.div>
        </aside>

        {/* ── RIGHT — sign-in panel ──────────────────────────── */}
        <main className="loginV2-panel">
          <div className="loginV2-panel-top">
            <button
              type="button"
              className="loginV2-themeBtn"
              onClick={toggleTheme}
              aria-label="Toggle color theme"
              title={theme === "light" ? "Switch to dark" : "Switch to light"}
            >
              {theme === "light" ? <Moon size={16}/> : <Sun size={16}/>}
            </button>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: showIntro ? 5.4 : 0.4, ease: "easeOut" }}
            className="loginV2-card"
          >
            <div className="loginV2-card-head">
              <h2>Welcome back</h2>
              <p>Sign in to continue to Gabruz Universe.</p>
            </div>

            <form onSubmit={submit} className="loginV2-form" noValidate>
              <label className="loginV2-field">
                <span>Email address</span>
                <div className="loginV2-field-wrap">
                  <Mail size={16} className="loginV2-field-icon"/>
                  <input
                    type="email"
                    autoComplete="email"
                    autoFocus={!DEMO_MODE && !showIntro}
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@yourclub.com"
                    required
                  />
                </div>
              </label>

              <label className="loginV2-field">
                <div className="loginV2-field-labelRow">
                  <span>Password</span>
                  <button
                    type="button"
                    className="loginV2-linkBtn"
                    onClick={() => setShowForgot(true)}
                  >Forgot password?</button>
                </div>
                <div className="loginV2-field-wrap">
                  <Lock size={16} className="loginV2-field-icon"/>
                  <input
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    className="loginV2-eyeBtn"
                    onClick={() => setShowPassword(s => !s)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff size={15}/> : <Eye size={15}/>}
                  </button>
                </div>
              </label>

              {error && (
                <div className="loginV2-error" role="alert">
                  {error}
                </div>
              )}

              <button
                className="loginV2-submit"
                type="submit"
                disabled={busy || !email || !password}
              >
                {busy ? "Signing in…" : <>Sign in <ArrowRight size={16}/></>}
              </button>

              <p className="loginV2-registerRow">
                New to Gabruz Universe?{" "}
                <button
                  type="button"
                  className="loginV2-registerLink"
                  onClick={() => setShowRegister(true)}
                >Apply to join</button>
              </p>
            </form>

            {DEMO_MODE && roleButtons.length > 0 && (
              <div className="loginV2-demo">
                <div className="loginV2-demo-head">
                  <span>Demo accounts</span>
                  <small>password: <code>gabruz123</code></small>
                </div>
                <div className="loginV2-demo-grid">
                  {roleButtons.map(({ role, user }) => (
                    <button
                      key={role}
                      type="button"
                      className="loginV2-demo-btn"
                      disabled={!user}
                      title={user ? `${user.name} (${user.email})` : "no seeded user for this role"}
                      onClick={() => pickRole(user)}
                    >
                      <strong>{role.replace("_", " ")}</strong>
                      {user && <small>{user.name}</small>}
                    </button>
                  ))}
                </div>

                <div className="loginV2-demo-versions" role="radiogroup" aria-label="UI version preview">
                  <span>Preview shell:</span>
                  {[{v:"v3",label:"Desktop"},{v:"v4",label:"Mobile"}].map(({v,label}) => (
                    <button
                      key={v}
                      type="button"
                      className={version === v ? "is-active" : ""}
                      onClick={() => setVersion(v)}
                      role="radio"
                      aria-checked={version === v}
                    >{label}</button>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </main>

        {/* ── Forgot password modal ────────────────────────────── */}
        {showForgot && (
          <div className="loginV2-modal" onClick={() => setShowForgot(false)}>
            <div className="loginV2-modal-inner" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
              <h3>Forgot your password?</h3>
              <p>
                Password reset isn't wired up yet — for now, ping an
                admin in your club and they can reset it for you from
                the Members page.
              </p>
              <button
                type="button"
                className="loginV2-submit"
                onClick={() => setShowForgot(false)}
              >Got it</button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
